import React, { useContext } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import NotFound from "../components/NotFound/NotFound";
import Unauthorized from "../components/Unauthorized/Unauthorized";
import Loader from "../components/AppLoader/Loader";
import DefaultLayouts from "../layouts/defaultLayouts";
import BlankLayouts from "../layouts/blankLayouts";
import AuthContext from "../hooks/authContext";
import { routes } from "../services/routes";
import PublicLayouts from "../layouts/publicLayouts";
import SubscriptionInvalidPage from "../components/Unauthorized/SubscriptionNotValid";
import LoginUser from "./auth/LoginUser";
import { getAccessKeyForRoute, hasAccess as hasPlanAccess } from "../services/accessControl";
import UpgradeRequiredPage from "../components/Unauthorized/UpgradeRequiredPage";
import { getEffectivePlanId, isFreemiumBillingState } from "../services/helpers";
import {
  getEffectiveUserRolesForAccess,
  hasUserRole,
  isCompanyManagerTeamAdminSeat,
  isRestrictedCompanyManagerBlockedPath,
  isRestrictedCompanyManager,
} from "../services/userRoleAccess";

/**
 * Manage App routes layout and authentication
 * @param {*} props
 * @returns
 */
const AppRoutes = ({ isLoading, ...props }) => {
  const auth = useContext(AuthContext);
  const substatus = auth?.substatus;
  const navigate = useNavigate();
  const rawRole = auth?.userProfile?.role;
  const employeeRole = auth?.userProfile?.employeerole;
  const companyManagerHasTeamAdminSeat =
    hasUserRole(auth?.userProfile?.role, "companyUser") &&
    isCompanyManagerTeamAdminSeat(auth?.userProfile);
  const restrictedCompanyManager = isRestrictedCompanyManager(auth?.userProfile);
  const isSystemAdminOnlyUser = auth?.userProfile?.role === "admin";
  const isSystemAdminScopeUser = isSystemAdminOnlyUser || restrictedCompanyManager;

  let userRoles = getEffectiveUserRolesForAccess(auth?.userProfile);
  // let userRoles = [...(Array.isArray(rawRole) ? rawRole : rawRole ? [rawRole] : [])]?.map((r) => (r === "Employee" && employeeRole ? employeeRole : r))?.filter(Boolean);
  // let userRoles = [...(rawRole === "Employee" && employeeRole ? [employeeRole] : [])]?.map((r) => (Array.isArray(rawRole) ? rawRole : rawRole ? [rawRole] : []))?.filter(Boolean);
  // userRoles = [...new Set(userRoles?.filter(Boolean))];

  const role =
    auth?.userProfile?.role === "Employee"
      ? auth?.userProfile?.employeerole
      : auth?.userProfile?.role;
  const allowedRoutesWithoutSubscription = ["/account/profile", "/peoples/manageEmp", "/peoples/inactiveEmp"];
  const allowedRoutesWithRestrictedBilling = new Set([
    "/account/profile",
    "/account/upgrade",
    "/account/companySubscription",
    "/account/settings",
    "/account/settings/privacy-data-settings",
  ]);
  const allowedRoutesForRestrictedCompanyManager = new Set([
    "/account/companySubscription",
    "/account/upgrade",
  ]);
  const SYSTEM_ADMIN_ALLOWED_PREFIXES = [
    "/",
    "/dashboard",
    "/newDashboard",
    "/peoples/",
    "/communications/message-board/live-chat",
    "/account/userAdmin",
    "/account/createUserAdmin",
    "/account/settings",
    "/account/profile",
    "/help/",
  ];
  const hasSystemAdminScopeAccess = (path, includeBilling = false) => {
    const routePath = String(path || "");
    const matchesSystemAdminScope = SYSTEM_ADMIN_ALLOWED_PREFIXES.some(
      (prefix) => routePath === prefix || routePath.startsWith(prefix)
    );
    if (matchesSystemAdminScope) return true;
    if (includeBilling) {
      return (
        routePath === "/account/companySubscription" ||
        routePath === "/account/upgrade"
      );
    }
    return false;
  };
  const billingAccessRestricted = auth?.userProfile?.billingAccessRestricted === true;
  const isFreemium = isFreemiumBillingState(auth?.userProfile || {});
  const userPackageId = getEffectivePlanId(auth?.userProfile || {});
  const userPlanAccess =
    auth?.userProfile?.planAccess ||
    auth?.userProfile?.plansAccess ||
    auth?.userProfile?.planaccess;
  const SOFT_UPGRADE_GATED_PKG_ACCESS = new Set([
    "SUPPLIES",
    "MANAGE_SUPPLIES",
    "ARCHIVE_SUPPLIES",
    "SUPPLY_SETTINGS",
  ]);
  const FREEMIUM_ALLOWED_REPORT_ROUTES = new Set([
    "/reports/Clientreport",
    "/reports/clientreport",
    "/reports/Employeereport",
    "/reports/employeereport",
    "/reports/ShiftReport",
    "/reports/Shiftreport",
    "/reports/Worksitereport",
    "/reports/Taskreport",
    "/reports/Checkincheckout",
    "/reports/newreport/clientreport",
    "/reports/newreport/employeereport",
    "/emailreports",
  ]);

  const CheckSecureRoute = (route) => {
    // Block route decisions until we know if the user is logged in. Once authenticated,
    // keep rendering the shell (avoid full-screen Loader) while profile/subscription work finishes.
    if (auth?.isLoading && auth?.status !== true) {
      return <Loader />;
    }

    // During fresh login, auth can become true before userProfile roles are hydrated.
    // Avoid rendering Unauthorized for that short window.
    const isEmployeeRoleHydrating =
      auth?.status === true &&
      (
        rawRole === "Employee" ||
        (Array.isArray(rawRole) && rawRole.includes("Employee"))
      ) &&
      !employeeRole;
    const isRoleHydrating = auth?.status === true && (userRoles.length === 0 || isEmployeeRoleHydrating);
    const isPackageHydrating =
      auth?.status === true &&
      route?.secure === true &&
      !!route?.pkgAccess &&
      !userRoles.includes("superUser") &&
      !userPackageId &&
      !isFreemium;
    const isAuthHydratingForSecureRoute =
      route?.secure === true && (isRoleHydrating || isPackageHydrating);

    const RouteComponent = route.component;
    // const role = auth?.userProfile?.role;
    const routeAccessKey = getAccessKeyForRoute(route?.pkgAccess);
    const hasRouteWebAccess =
      !routeAccessKey || hasPlanAccess(userPlanAccess, routeAccessKey);
    const allowSoftGatedPage =
      !restrictedCompanyManager &&
      userRoles.includes("companyUser") &&
      SOFT_UPGRADE_GATED_PKG_ACCESS.has(route?.pkgAccess);
    const hasPackageAccess =
      !route?.pkgAccess ||
      userRoles.includes("superUser") ||
      allowSoftGatedPage ||
      hasRouteWebAccess;
    const restrictedCompanyManagerHasAdminAccess =
      companyManagerHasTeamAdminSeat &&
      Array.isArray(route?.roles) &&
      route.roles.includes("admin");
    const restrictedCompanyManagerHasBillingAccess =
      restrictedCompanyManager &&
      allowedRoutesForRestrictedCompanyManager.has(route.path);
    const restrictedCompanyManagerBlocked =
      restrictedCompanyManager &&
      isRestrictedCompanyManagerBlockedPath(route.path);
    const hasRoleAccess =
      !restrictedCompanyManagerBlocked &&
      (
        restrictedCompanyManagerHasAdminAccess ||
        restrictedCompanyManagerHasBillingAccess ||
        route?.roles?.some((r) => userRoles.includes(r))
      );


    if (userRoles.includes("superUser")) {
      if (auth.status === true && (hasRoleAccess || isAuthHydratingForSecureRoute)) {
        return <RouteComponent {...props} />;
      } else {
        if (auth?.status !== true && auth?.loginBack === true) {
          return <LoginUser />;
        } else {
          return <Unauthorized />;
        }
      }
    }

    if (route.secure === true) {
      if (auth.status === true && (hasRoleAccess || isAuthHydratingForSecureRoute)) {
        if (!hasPackageAccess && !isAuthHydratingForSecureRoute) {
          if (userRoles.includes("companyUser")) {
            return (
              <UpgradeRequiredPage
                title="Feature not included in your plan"
                description="This page is currently restricted for your plan. Upgrade to continue."
              />
            );
          }
          return <SubscriptionInvalidPage />;
        }
        if (
          isFreemium &&
          route.path?.startsWith("/reports") &&
          !FREEMIUM_ALLOWED_REPORT_ROUTES.has(route.path) &&
          !isAuthHydratingForSecureRoute
        ) {
          return (
            <UpgradeRequiredPage
              title="Report not included in your plan"
              description="This report is available on an upgraded plan."
            />
          );
        }
        if (
          substatus === true &&
          !allowedRoutesWithoutSubscription.includes(route.path) &&
          !isAuthHydratingForSecureRoute
        ) {
          if (userRoles.includes("companyUser")) {
            return (
              <UpgradeRequiredPage
                title="Subscription update required"
                description="Your subscription needs an update to access this page. Upgrade your plan to continue."
              />
            );
          } else {
            return <SubscriptionInvalidPage />;
          }
        }
        if (
          billingAccessRestricted &&
          !allowedRoutesWithRestrictedBilling.has(route.path) &&
          !isAuthHydratingForSecureRoute
        ) {
          if (userRoles.includes("companyUser")) {
            return (
              <UpgradeRequiredPage
                title="Subscription cancelled"
                description="Feature access is disabled after cancellation. You can still manage billing, reactivate your plan, or use privacy settings."
              />
            );
          }
          return <SubscriptionInvalidPage />;
        }
        if (
          isSystemAdminScopeUser &&
          !isAuthHydratingForSecureRoute &&
          (
            restrictedCompanyManagerBlocked ||
            !hasSystemAdminScopeAccess(route.path, restrictedCompanyManager)
          )
        ) {
          return <Unauthorized />;
        }
        return <RouteComponent {...props} />;
      } else {
        // If auth is strictly NOT true and we finished loading, then decide:
        if (auth?.status !== true && auth?.loginBack === true) {
          return <LoginUser />;
        } else {
          return <Unauthorized />;
        }
      }
    }
    return <RouteComponent {...props} />;
  };

  const routerItem = (route) => {
    if (route.layout === "none") {
      // Fully public — no layout wrapper, no auth redirect (e.g. review form, redirect pages)
      return (
        <Route
          key={route.index}
          path={route.path}
          element={CheckSecureRoute(route)}
          exact={route.exact}
        />
      );
    } else if (route.layout === "blank") {
      return (
        <Route
          key={route.index}
          path={route.path}
          element={
            <BlankLayouts {...props}>{CheckSecureRoute(route)}</BlankLayouts>
          }
          render={(props) => (
            <BlankLayouts {...props}>{CheckSecureRoute(route)}</BlankLayouts>
          )}
          exact={route.exact}
        />
      );
    } else if (route.layout === "public") {
      return (
        <Route
          key={route.index}
          path={route.path}
          element={
            <PublicLayouts {...props}>{CheckSecureRoute(route)}</PublicLayouts>
          }
          render={(props) => (
            <PublicLayouts {...props}>{CheckSecureRoute(route)}</PublicLayouts>
          )}
          exact={route.exact}
        />
      );
    } else {
      return (
        <Route
          key={route.index}
          path={route.path}
          element={
            <DefaultLayouts {...props} isSecure={true} isLoading={isLoading}>
              {CheckSecureRoute(route)}
            </DefaultLayouts>
          }
          render={(props) => (
            <DefaultLayouts {...props} isSecure={true} isLoading={isLoading}>
              {CheckSecureRoute(route)}
            </DefaultLayouts>
          )}
          exact={route.exact}
        />
      );
    }
  };

  const renderRoutes = () => {
    return routes?.map((route) => {
      return routerItem(route);
    });
  };

  // Lazy route chunks suspend inside layout wrappers (see DefaultLayouts / PublicLayouts /
  // BlankLayouts) so the shell + sidebar stay mounted — avoids a full blank flash on first nav.
  return (
    <Routes>
      {renderRoutes()}
      <Route
        path="*"
        element={
          <DefaultLayouts {...props} isSecure={true}>
            <NotFound {...props} />
          </DefaultLayouts>
        }
      />
    </Routes>
  );
};

export const withRouter = (Component) => {
  const Wrapper = (props) => {
    const history = useNavigate();
    return <Component history={history} {...props} />;
  };
  return Wrapper;
};

export default withRouter(AppRoutes);
